/**
 * Génération d'identifiants UUID version 7.
 *
 * Deux raisons de ne pas utiliser `crypto.randomUUID()` :
 *
 *  1. Il n'existe que dans un contexte sécurisé. Servie en HTTP sur une adresse IP — l'état
 *     actuel, faute de domaine (point ouvert P1) — l'application n'en dispose pas, et l'appel
 *     échoue avant même que la requête ne parte.
 *  2. Il produit du v4, purement aléatoire, alors que docs/02-schema-donnees.md impose du v7 :
 *     les 48 premiers bits portent l'horodatage, ce qui donne aux clés primaires la localité
 *     d'index d'un compteur séquentiel sans exposer de compteur.
 *
 * `crypto.getRandomValues`, lui, est disponible hors contexte sécurisé.
 */
export function uuidV7(now: number = Date.now()): string {
  const bytes = new Uint8Array(16)
  crypto.getRandomValues(bytes)

  const view = new DataView(bytes.buffer)

  // 48 bits d'horodatage en millisecondes, en tête et en gros-boutien : deux identifiants
  // produits à la suite se trient donc dans l'ordre de leur création.
  view.setUint16(0, Math.floor(now / 2 ** 32))
  view.setUint32(2, now >>> 0)

  // Version 7 sur les quatre bits hauts de l'octet 6.
  view.setUint8(6, (view.getUint8(6) & 0x0f) | 0x70)
  // Variante RFC 9562 sur les deux bits hauts de l'octet 8.
  view.setUint8(8, (view.getUint8(8) & 0x3f) | 0x80)

  const hex = Array.from(bytes, (octet) => octet.toString(16).padStart(2, '0')).join('')

  return [
    hex.slice(0, 8),
    hex.slice(8, 12),
    hex.slice(12, 16),
    hex.slice(16, 20),
    hex.slice(20),
  ].join('-')
}
